import React from 'react';
import { colors } from '@skyslope/mache';
import useUserInfo from '../../auth/useUserInfo';
import { RecipientRole, type IRecipient } from '../../common/recipients/types';
import { checkIfRecipientIsSender } from '../../common/utils';
import { useGetRecipients } from '../../lib/api/envelope';
import { useDispatch, useShallowSelector } from '../../lib/reduxHooks';
import * as actions from '../../store/senderBuild/actions';

export function useInitRecipientManagement(args: {
  envelopeId: string;
  modal: boolean | undefined;
  setRecipients: React.Dispatch<React.SetStateAction<IRecipient[]>>;
  setIncompletePlaceholders: React.Dispatch<React.SetStateAction<IRecipient[] | undefined>>;
  setOptionalRecipients: React.Dispatch<React.SetStateAction<IRecipient[] | undefined>>;
}) {
  const { envelopeId, modal, setRecipients, setIncompletePlaceholders, setOptionalRecipients } = args;

  const { signers, contacts, settingsFetched } = useShallowSelector((state) => ({
    signers: state.senderBuild.signers,
    settings: state.pageFrame.settings,
    settingsFetched: state.pageFrame.settingsFetched,
    colorCounter: state.pageFrame.recipientColorCounter,
    contacts: state.senderBuild.recipientsModal.contacts,
  }));

  const dispatch = useDispatch();
  const userInfo = useUserInfo();

  const isInitializedRef = React.useRef(false);
  const initialRecipients = useGetRecipients({ envelopeId, signers, allRecipients: true });

  React.useEffect(() => {
    if (!contacts.length) dispatch(actions.fetchContacts());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  React.useEffect(() => {
    if (isInitializedRef.current || !settingsFetched || !userInfo || initialRecipients.isFetching) return;
    isInitializedRef.current = true;

    const recipients = initialRecipients.data ?? [];
    setRecipients(recipients);

    const sender: IRecipient = {
      id: userInfo!.senderId,
      role: RecipientRole.Signer,
      firstName: userInfo!.senderFirstName!,
      lastName: userInfo!.senderLastName!,
      email: userInfo!.senderEmail!,
      isSender: true,
      color: colors.blue[800],
      isActive: false,
      isPlaceholder: false,
      title: 'Me',
      recipientColorCounter: 0,
    };
    validateSenderColor({ recipients, sender, senderEmail: userInfo.senderEmail });

    const nextOptionalRecipients = modal
      ? []
      : recipients.filter((r) => r.isPlaceholder && (r.firstName || r.email) && !checkIfRecipientIsSender(r));
    const isMeRecipient = addSenderToOptional(nextOptionalRecipients, recipients, sender);
    setOptionalRecipients(nextOptionalRecipients);

    const nextIncompletePlaceholders = getIncompletePlaceHolders(recipients, isMeRecipient);
    setIncompletePlaceholders(nextIncompletePlaceholders);
  }, [
    settingsFetched,
    userInfo,
    initialRecipients.isFetching,
    initialRecipients.data,
    setRecipients,
    modal,
    setOptionalRecipients,
    setIncompletePlaceholders,
  ]);

  return initialRecipients.isLoading;
}

// Filters Me from incomplete placeholders if needed
function getIncompletePlaceHolders(recipients: IRecipient[], isMeRecipient: boolean) {
  const incompletePlaceholders = recipients.filter(
    (r) => r.isPlaceholder && (r.firstName === null || r.email === null)
  );

  return isMeRecipient
    ? incompletePlaceholders?.filter((r) => r.title?.toLocaleLowerCase() !== 'me')
    : incompletePlaceholders;
}

function validateSenderColor(args: { recipients: IRecipient[]; sender: IRecipient; senderEmail: string }) {
  const { recipients, sender, senderEmail } = args;

  // Validate sender recipient color is correct. The default color is blue[800], but if this recipient gets saved to the envelope
  // it could have a different placeholder. Carbon Copy recipients don't have color correctly assigned to them. We have a ticket to address this.
  // To get around this, if a sender recipient is active and also a CC they won't have a color. This check below will handle that case until colors are fixed.
  const senderRecipient = recipients.find(
    (r) => r.email?.toLowerCase() === senderEmail.toLowerCase() && r.title?.toLowerCase() === 'me'
  );
  if (senderRecipient && (senderRecipient.color || senderRecipient?.role === RecipientRole.CarbonCopy)) {
    sender.color = senderRecipient.color;
    sender.isActive = true;
    sender.role = senderRecipient.role;
  }
}

/**
 * Adds sender as Me placeholder
 * @returns True if the me recipient was found, false otherwise
 */
function addSenderToOptional(
  nextOptionalRecipients: IRecipient[],
  nextRecipients: IRecipient[],
  sender: IRecipient
): boolean {
  if (nextOptionalRecipients.some((or) => checkIfRecipientIsSender(or))) return false;

  // Add sender as optionalRecipients
  nextOptionalRecipients.unshift(sender);

  // Check if me is a placeholder
  const meRecipient = nextRecipients.find((r: IRecipient) => r.title?.toLowerCase() === 'me');
  if (!meRecipient) return false;

  const meIndex = nextRecipients.indexOf(meRecipient);
  const isMeRecipient = meIndex >= 0;
  if (!isMeRecipient) return false;

  // If recipient hasn't been assigned before, the email in recipient Me should be empty
  if (!nextRecipients[meIndex].email) {
    nextOptionalRecipients[0].id = nextRecipients[meIndex].id;
    nextOptionalRecipients[0].group = { id: 'default', name: 'default', order: 0 };
    nextOptionalRecipients[0].isActive = true;
    nextOptionalRecipients[0].isPlaceholder = true;
    nextRecipients[meIndex] = { ...nextOptionalRecipients[0] };
  } else {
    nextRecipients[meIndex].title = nextRecipients[meIndex].title ?? 'Me';
    nextRecipients[meIndex].isSender = true;
    nextRecipients[meIndex].group = nextRecipients[meIndex].group ?? { id: 'default', name: 'default', order: 0 };
    nextRecipients[meIndex].color = colors.blue[800];
    nextOptionalRecipients[0] = { ...nextRecipients[meIndex] };
  }

  return true;
}
